import React from 'react';
import { Calendar, Clock, Users, Phone, Mail, ShoppingBag, Star, X, Check, Plus, Minus, Loader2, AlertCircle, CreditCard, ChevronRight, MapPin, Utensils } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { ReservationData, OrderFormData, FeedbackData, MenuItem } from '../types';
import { useCreateOrder, useCreateReservation, useSubmitReview } from '../hooks/useSupabaseOps';

// --- Reusable Styled Components ---

const FloatingInput = ({
  label,
  urduLabel,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = " ",
  className = "",
  icon: Icon,
  error = false
}: any) => (
  <div className={`relative z-0 w-full mb-8 group ${className} ${error ? 'animate-shake' : ''}`}>
    {Icon && <Icon size={18} className="absolute left-0 top-3 text-gray-400 group-focus-within:text-terracotta transition-colors duration-300" />}
    <input
      type={type}
      value={value}
      onChange={onChange}
      className={`block py-2.5 ${Icon ? 'pl-8' : 'pl-0'} pr-0 w-full text-base text-gray-900 bg-transparent border-0 border-b-2 ${error ? 'border-red-500' : 'border-gray-200'} appearance-none focus:outline-none focus:ring-0 focus:border-terracotta peer transition-all duration-300 font-medium placeholder-transparent`}
      placeholder={placeholder}
      required={required}
    />
    <label className={`peer-focus:font-bold absolute text-xs text-gray-500 duration-300 transform -translate-y-5 scale-90 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-terracotta peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 ${Icon ? 'peer-placeholder-shown:translate-x-8' : ''} peer-focus:scale-90 peer-focus:-translate-y-5 peer-focus:translate-x-0 flex items-baseline gap-1.5`}>
      {label} {urduLabel && <span className="text-[10px] font-normal text-gray-400 italic">/ {urduLabel}</span>}
    </label>
    {error && <p className="absolute -bottom-5 left-0 text-[10px] font-bold text-red-500">Required field</p>}
  </div>
);

const FloatingSelect = ({
  label,
  urduLabel,
  value,
  onChange,
  options,
  required = false,
  icon: Icon
}: any) => (
  <div className="relative z-0 w-full mb-8 group">
    {Icon && <Icon size={18} className="absolute left-0 top-3 text-gray-400 group-focus-within:text-terracotta transition-colors duration-300" />}
    <select
      value={value}
      onChange={onChange}
      className={`block py-2.5 ${Icon ? 'pl-8' : 'pl-0'} pr-0 w-full text-base text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-terracotta peer transition-all duration-300 font-medium bg-none cursor-pointer`}
      required={required}
    >
      <option value="" disabled hidden> </option>
      {options.map((opt: any) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <label className={`peer-focus:font-bold absolute text-xs text-gray-500 duration-300 transform -translate-y-5 scale-90 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-terracotta peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 ${Icon ? 'peer-placeholder-shown:translate-x-8' : ''} peer-focus:scale-90 peer-focus:-translate-y-5 peer-focus:translate-x-0 flex items-baseline gap-1.5`}>
      {label} {urduLabel && <span className="text-[10px] font-normal text-gray-400 italic">/ {urduLabel}</span>}
    </label>
    <ChevronRight size={16} className="absolute right-0 top-4 text-gray-400 rotate-90 pointer-events-none" />
  </div>
);

const FloatingTextarea = ({ label, urduLabel, value, onChange, rows = 3 }: any) => (
  <div className="relative z-0 w-full mb-8 group">
    <textarea
      rows={rows}
      value={value}
      onChange={onChange}
      className="block py-2.5 px-0 w-full text-base text-gray-900 bg-transparent border-0 border-b-2 border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-terracotta peer resize-none transition-all duration-300 font-medium placeholder-transparent"
      placeholder=" "
    />
    <label className="peer-focus:font-bold absolute text-xs text-gray-500 duration-300 transform -translate-y-5 scale-90 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-terracotta peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-90 peer-focus:-translate-y-5 flex items-baseline gap-1.5">
      {label} {urduLabel && <span className="text-[10px] font-normal text-gray-400 italic">/ {urduLabel}</span>}
    </label>
  </div>
);

// --- Reservation Form ---

interface ReservationFormProps {
  data: ReservationData;
  onChange: (field: keyof ReservationData, value: any) => void;
  onSubmit: (result?: any) => void;
  onCancel: () => void;
}

export const ReservationForm: React.FC<ReservationFormProps> = ({ data, onChange, onSubmit, onCancel }) => {
  const { bookTable, loading, error } = useCreateReservation();

  const timeSlots = [
    { value: '17:00', label: '5:00 PM' },
    { value: '17:30', label: '5:30 PM' },
    { value: '18:00', label: '6:00 PM' },
    { value: '18:30', label: '6:30 PM' },
    { value: '19:00', label: '7:00 PM' },
    { value: '19:30', label: '7:30 PM' },
    { value: '20:00', label: '8:00 PM' },
    { value: '20:30', label: '8:30 PM' },
    { value: '21:00', label: '9:00 PM' },
  ];

  const handleConfirm = async () => {
    if (!data.name || !data.phone || !data.date || !data.time) return;
    try {
      const result = await bookTable(data);
      onSubmit({ type: 'reservation', data, result });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50/50">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
        <div>
          <h3 className="font-serif font-bold text-2xl text-gray-900 tracking-tight">Reserve a Table</h3>
          <p className="text-xs text-terracotta font-bold tracking-widest uppercase mt-1.5 flex items-center gap-1">
            <Utensils size={12} /> Fine Dining Experience
          </p>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl text-sm flex items-start gap-3 animate-in slide-in-from-top-2 fade-in duration-300 shadow-sm">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="section-card p-6 space-y-4">
          <div className="grid grid-cols-2 gap-x-6">
            <div className="relative">
              <FloatingInput
                label="Date"
                urduLabel="تاریخ"
                type="date"
                value={data.date}
                onChange={(e: any) => onChange('date', e.target.value)}
                required
                icon={Calendar}
              />
              {data.date && (['03-23', '08-14', '12-25', '03-20', '03-21', '05-27', '05-28'].some(h => data.date.endsWith(h))) && (
                <span className="absolute -bottom-1 left-0 text-[10px] text-orange-600 font-bold flex items-center gap-1 animate-pulse">
                  <AlertCircle size={10} /> High Demand Day (Eid/Holiday)
                </span>
              )}
            </div>
            <FloatingSelect
              label="Time"
              value={data.time}
              onChange={(e: any) => onChange('time', e.target.value)}
              options={timeSlots}
              required
              icon={Clock}
            />
          </div>

          <div className="relative z-0 w-full group pt-2">
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-500 font-medium flex items-center gap-2"><Users size={18} /> Number of Guests</span>
              <div className="flex items-center gap-4 bg-gray-50 rounded-full p-1 border border-gray-100 shadow-inner">
                <button onClick={() => onChange('guests', Math.max(1, data.guests - 1))} className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-gray-600 shadow-sm hover:text-terracotta hover:scale-105 active:scale-95 transition-all duration-200 border border-gray-100"><Minus size={16} /></button>
                <span className="font-serif font-bold text-xl text-gray-900 w-8 text-center tabular-nums">{data.guests}</span>
                <button onClick={() => onChange('guests', Math.min(20, data.guests + 1))} className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-gray-600 shadow-sm hover:text-terracotta hover:scale-105 active:scale-95 transition-all duration-200 border border-gray-100"><Plus size={16} /></button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
          <h4 className="font-serif font-bold text-gray-900 border-b border-gray-50 pb-2 mb-4">Contact Details</h4>
          <FloatingInput
            label="Full Name"
            urduLabel="کمل نام"
            value={data.name}
            onChange={(e: any) => onChange('name', e.target.value)}
            placeholder="e.g. Ahmed Ali"
            required
          />

          <div className="grid grid-cols-2 gap-x-6">
            <FloatingInput
              label="Mobile Number"
              urduLabel="موبائل نمبر"
              type="tel"
              value={data.phone}
              onChange={(e: any) => onChange('phone', e.target.value)}
              placeholder="+92 301 2345678"
              required
              icon={Phone}
            />
            <FloatingInput
              label="Email"
              urduLabel="ای میل"
              type="email"
              value={data.email}
              onChange={(e: any) => onChange('email', e.target.value)}
              icon={Mail}
            />
          </div>

          <FloatingSelect
            label="Occasion"
            urduLabel="تقریب"
            value={data.occasion || ''}
            onChange={(e: any) => onChange('occasion', e.target.value)}
            options={[
              { value: 'Birthday', label: 'Birthday' },
              { value: 'Anniversary', label: 'Anniversary' },
              { value: 'Mehndi/Walima', label: 'Mehndi/Walima' },
              { value: 'Family Dinner', label: 'Family Dinner' },
              { value: 'Business Meeting', label: 'Business Meeting' },
              { value: 'Ramadan Iftar', label: 'Ramadan Iftar' },
              { value: 'Other', label: 'Other' },
            ]}
          />

          <FloatingSelect
            label="Seating Preference"
            urduLabel="بیٹھنے کی جگہ"
            value={data.seating_preference || 'Family Hall'}
            onChange={(e: any) => onChange('seating_preference', e.target.value)}
            options={[
              { value: 'Family Hall', label: 'Family Hall' },
              { value: 'Couple Section', label: 'Couple Section' },
              { value: 'Rooftop/Garden Seating', label: 'Rooftop/Garden Seating' },
              { value: 'Private Room', label: 'Private Room (8+ Guests)' },
              { value: 'AC Hall', label: 'AC Hall' },
            ]}
          />

          <FloatingTextarea
            label="Special Requests (Optional)"
            urduLabel="خصوصی درخواست"
            value={data.specialRequests || ''}
            onChange={(e: any) => onChange('specialRequests', e.target.value)}
            placeholder="Jaise high chair, wheelchair access, allergy info"
          />
        </div>
      </div>

      <div className="p-6 bg-white/50 border-t border-white/20">
        <button
          onClick={handleConfirm}
          disabled={loading || !data.name || !data.phone || !data.date || !data.time}
          className="w-full terracotta-gradient hover:shadow-xl hover:shadow-terracotta/20 text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:-translate-y-1 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
        >
          {loading ? <Loader2 className="animate-spin" /> : <>Complete Reservation (بکنگ مکمل کریں) <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" /></>}
        </button>
      </div>
    </div>
  );
};

// --- Order Form ---

interface OrderFormProps {
  data: OrderFormData;
  onChange: (field: keyof OrderFormData, value: any) => void;
  onSubmit: (result?: any) => void;
  onCancel: () => void;
}

export const OrderForm: React.FC<OrderFormProps> = ({ data, onChange, onSubmit, onCancel }) => {
  const { cart, updateQuantity, cartTotal, clearCart } = useCart();
  const { placeOrder, loading, error } = useCreateOrder();

  const tax = cartTotal * 0.13;
  const total = cartTotal + tax;

  const handlePlaceOrder = async () => {
    if (!data.name || !data.phone) return;
    try {
      const result = await placeOrder(data.name, data.phone, data.email, cart, data, cartTotal);
      clearCart();
      onSubmit({ type: 'order', data, result });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50/50">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
        <div>
          <h3 className="font-serif font-bold text-2xl text-gray-900 tracking-tight">Order Summary</h3>
          <p className="text-xs text-terracotta font-bold tracking-widest uppercase mt-1.5 flex items-center gap-1">
            <ShoppingBag size={12} /> {cart.length} Items in Cart
          </p>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar">
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl text-sm flex items-start gap-3 mb-6 animate-in slide-in-from-top-2 fade-in duration-300 shadow-sm">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Order Items */}
        <div className="space-y-3 mb-8 overflow-y-auto pr-1">
          {cart.length === 0 ? (
            <div className="text-center py-12 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100 animate-in fade-in zoom-in">
              <ShoppingBag size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="font-medium text-gray-400">Your cart is empty</p>
            </div>
          ) : (
            cart.map((item, idx) => (
              <div key={item.id} className="flex justify-between items-center bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group animate-in slide-in-from-right-8 fade-in" style={{ animationDelay: `${idx * 0.05}s` }}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden shadow-inner relative flex-shrink-0">
                    {item.image ? (
                      <img src={item.image} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    ) : <div className="w-full h-full bg-gray-200 animate-pulse" />}
                  </div>
                  <div>
                    <p className="font-bold text-base text-gray-900 font-serif leading-tight">{item.name}</p>
                    <p className="text-sm text-terracotta font-bold mt-1.5">₨{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-2 py-1.5 border border-gray-100">
                  <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-400 hover:text-terracotta transition-colors active:scale-95"><Minus size={14} /></button>
                  <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm text-gray-400 hover:text-terracotta transition-colors active:scale-95"><Plus size={14} /></button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Form Fields */}
        <div className="section-card p-6 space-y-4">
          <h4 className="font-serif font-bold text-gray-900 border-b border-gray-50 pb-2 mb-4 flex justify-between items-baseline">
            Checkout Details
            <span className="text-[10px] text-gray-400 font-sans italic">ادائیگی کی تفصیلات</span>
          </h4>
          <FloatingInput
            label="Full Name"
            urduLabel="کمل نام"
            value={data.name}
            onChange={(e: any) => onChange('name', e.target.value)}
            placeholder="e.g. Ahmed Ali"
            required
          />
          <FloatingInput
            label="Mobile Number"
            urduLabel="موبائل نمبر"
            type="tel"
            value={data.phone}
            onChange={(e: any) => onChange('phone', e.target.value)}
            placeholder="+92 301 2345678"
            required
            icon={Phone}
          />

          <div className="grid grid-cols-2 gap-x-6">
            <FloatingSelect
              label="Order Type"
              urduLabel="آرڈر کی قسم"
              value={data.orderType || 'pickup'}
              onChange={(e: any) => onChange('orderType', e.target.value)}
              options={[
                { value: 'pickup', label: 'Pickup (پک اپ)' },
                { value: 'delivery', label: 'Delivery (ڈیلیوری)' },
                { value: 'dine-in', label: 'Dine-in (ڈائن ان)' },
              ]}
              icon={Utensils}
            />
            <FloatingSelect
              label="Time"
              urduLabel="وقت"
              value={data.pickupTime}
              onChange={(e: any) => onChange('pickupTime', e.target.value)}
              options={[
                { value: 'ASAP', label: 'ASAP (جلد از جلد)' },
                { value: '15m', label: '15 Mins (15 منٹ)' },
                { value: '30m', label: '30 Mins (30 منٹ)' },
                { value: '1h', label: '1 Hour (1 گھنٹہ)' },
              ]}
              icon={Clock}
            />
          </div>

          <FloatingSelect
            label="Payment"
            urduLabel="ادائیگی"
            value={data.paymentMethod}
            onChange={(e: any) => onChange('paymentMethod', e.target.value)}
            options={[
              { value: 'pickup', label: 'Cash on Pickup/Delivery (کیش آن پک اپ/ڈیلیوری)' },
              { value: 'easypaisa', label: 'Easypaisa (ایزی پیسہ)' },
              { value: 'jazzcash', label: 'JazzCash (جاز کیش)' },
              { value: 'bank', label: 'Bank Transfer (بینک ٹرانسفر)' },
            ]}
            icon={CreditCard}
          />

          <FloatingTextarea
            label="Special Instructions"
            urduLabel="خصوصی ہدایات"
            value={data.specialInstructions}
            onChange={(e: any) => onChange('specialInstructions', e.target.value)}
            placeholder="e.g. Extra raita, less spicy, etc. (خاص ہدایات (اضافی رائتہ، کم مرچ، وغیرہ))"
            rows={2}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 bg-white/50 border-t border-white/20">
        <div className="flex justify-between items-end mb-4 px-1">
          <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Total <span className="text-[10px] italic">/ کل رقم</span></span>
          <span className="text-3xl font-serif font-bold text-gray-900 tracking-tight animate-price-change" key={total}>
            ₨{total.toFixed(2)}
          </span>
        </div>
        <button
          onClick={handlePlaceOrder}
          disabled={loading || cart.length === 0 || !data.name || !data.phone}
          className="w-full terracotta-gradient hover:shadow-xl hover:shadow-terracotta/20 text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:-translate-y-1 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
        >
          {loading ? <Loader2 className="animate-spin" /> : <>Place Order (آرڈر دیں) <CreditCard size={20} className="group-hover:rotate-12 transition-transform duration-300" /></>}
        </button>
      </div>
    </div>
  );
};

// --- Feedback Form ---

interface FeedbackFormProps {
  data: FeedbackData;
  onChange: (field: keyof FeedbackData, value: any) => void;
  onSubmit: (result?: any) => void;
  onCancel: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ data, onChange, onSubmit, onCancel }) => {
  const { postReview, loading } = useSubmitReview();
  const tags = [
    { id: 'Food Quality', label: 'Food Quality (کھانے کی کوالٹی)' },
    { id: 'Spice Level', label: 'Spice Level (مصالحے کی سطح)' },
    { id: 'Portion Size', label: 'Portion Size (مقدار)' },
    { id: 'Service Speed', label: 'Service Speed (سروس کی رفتار)' },
    { id: 'Cleanliness', label: 'Cleanliness (صفائی)' },
    { id: 'Value for Money', label: 'Value for Money (پیسے کی قدر)' }
  ];

  const toggleTag = (tag: string) => {
    const newTags = data.tags.includes(tag)
      ? data.tags.filter(t => t !== tag)
      : [...data.tags, tag];
    onChange('tags', newTags);
  };

  const handleSubmit = async () => {
    try {
      if (!data.name || !data.phone) {
        alert("Please enter your name and phone number.");
        return;
      }
      await postReview(data);
      onSubmit({ type: 'feedback', data });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50/30">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center z-10">
        <div className="w-10"></div> {/* Spacer for centering */}
        <div className="text-center">
          <h3 className="font-serif font-bold text-2xl text-gray-900 tracking-tight">Rate Experience</h3>
          <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">We value your feedback (ہم آپ کی رائے کی قدر کرتے ہیں)</p>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow flex flex-col items-center space-y-8 overflow-y-auto px-6 custom-scrollbar pb-6">
        <div className="flex gap-2 my-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-50">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => onChange('rating', star)}
              className="transition-transform hover:scale-110 active:scale-95 focus:outline-none group p-1"
            >
              <Star
                size={42}
                className={`${star <= data.rating ? 'fill-gold text-gold drop-shadow-md' : 'fill-gray-50 text-gray-200'} transition-all duration-300`}
                strokeWidth={1.5}
              />
            </button>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-2 max-w-sm">
          {tags.map(tag => (
            <button
              key={tag.id}
              onClick={() => toggleTag(tag.id)}
              className={`px-5 py-2.5 rounded-full text-[10px] font-bold border transition-all duration-300 ${data.tags.includes(tag.id)
                ? 'bg-terracotta text-white border-terracotta shadow-md transform scale-105'
                : 'bg-white text-gray-500 border-gray-200 hover:border-terracotta/50 hover:text-terracotta hover:bg-gray-50'
                }`}
            >
              {tag.label}
            </button>
          ))}
        </div>

        <div className="section-card p-6 space-y-6">
          <div className="flex flex-col items-center gap-6">
            <FloatingTextarea
              label="Share details"
              urduLabel="تفصیلات بتائیں"
              value={data.review}
              onChange={(e: any) => onChange('review', e.target.value)}
              rows={4}
              placeholder="اپنے خیالات کا اظہار کریں"
            />
          </div>

          <div className="space-y-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-2"><Check size={14} /> Verification <span className="italic font-normal">/ تصدیق</span></p>
            <FloatingInput
              label="Full Name"
              urduLabel="کمل نام"
              value={data.name || ''}
              onChange={(e: any) => onChange('name', e.target.value)}
              required
            />
            <FloatingInput
              label="Mobile Number"
              urduLabel="موبائل نمبر"
              type="tel"
              value={data.phone || ''}
              onChange={(e: any) => onChange('phone', e.target.value)}
              placeholder="+92 301 2345678"
              required
              icon={Phone}
            />
            <div className="flex items-center gap-3 pt-2">
              <div className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={!data.showName}
                  onChange={(e: any) => onChange('showName', !e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none ring-0 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-terracotta"></div>
                <label htmlFor="anonymous" className="ml-3 text-sm font-bold text-gray-600">Anonymous review (گمنام جائزہ)</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 bg-white border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <button
          onClick={handleSubmit}
          disabled={data.rating === 0 || loading || !data.name || !data.phone}
          className="w-full bg-gold hover:bg-yellow-600 disabled:bg-gray-200 disabled:text-gray-400 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:shadow-gold/20 transition-all duration-300 transform hover:-translate-y-0.5 active:scale-95 flex justify-center items-center gap-2"
        >
          {loading ? <Loader2 className="animate-spin" /> : <>Submit Feedback (رائے جمع کروائیں) <Check size={20} /></>}
        </button>
      </div>
    </div>
  );
};

// --- Menu Display ---

interface MenuDisplayProps {
  items: MenuItem[];
  onAddToCart: (item: MenuItem) => void;
  onCancel: () => void;
}

export const MenuDisplay: React.FC<MenuDisplayProps> = ({ items, onAddToCart, onCancel }) => {
  const { cartCount } = useCart();

  return (
    <div className="h-full flex flex-col bg-gray-50/50">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="font-serif font-bold text-2xl text-gray-900 tracking-tight">Our Menu</h3>
            <p className="text-xs text-terracotta font-bold tracking-widest uppercase mt-1.5">Curated Selections</p>
          </div>
          {cartCount > 0 && (
            <div className="bg-terracotta text-white px-3 py-1 rounded-full text-xs font-bold shadow-md animate-bounce">
              {cartCount}
            </div>
          )}
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <div className="bg-gray-100 p-6 rounded-full mb-4">
              <ShoppingBag size={48} className="opacity-20" />
            </div>
            <p className="text-lg font-serif">No items found.</p>
          </div>
        ) : (
          items.map((item, idx) => (
            <div
              key={item.id}
              className="flex gap-4 p-4 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300 group animate-in slide-in-from-bottom-4 fade-in fill-mode-backwards transform hover:-translate-y-1"
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              <div className="w-28 h-28 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 relative shadow-inner">
                {item.image ? (
                  <>
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 ease-out" />
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 font-medium">No Image</div>
                )}
              </div>
              <div className="flex-grow flex flex-col justify-between py-0.5">
                <div>
                  <h4 className="font-bold text-gray-900 line-clamp-1 font-serif text-lg group-hover:text-terracotta transition-colors">{item.name}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-1 leading-relaxed">{item.description}</p>
                  {item.dietary && item.dietary.length > 0 && (
                    <div className="flex gap-1.5 mt-2">
                      {item.dietary.map(d => (
                        <span key={d} className="text-[10px] font-bold bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-100">{d}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-end mt-2">
                  <span className="font-bold text-gray-900 text-xl tracking-tight">₨{item.price}</span>
                  <button
                    onClick={() => onAddToCart(item)}
                    className="text-xs bg-gray-900 text-white px-4 py-2.5 rounded-full font-bold transition-all flex items-center gap-1.5 hover:bg-terracotta shadow-md hover:shadow-lg active:scale-95 transform group-hover:shadow-terracotta/20"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// --- Comparison Display ---

interface ComparisonDisplayProps {
  items: MenuItem[];
  onAddToCart: (item: MenuItem) => void;
  onCancel: () => void;
}

export const ComparisonDisplay: React.FC<ComparisonDisplayProps> = ({ items, onAddToCart, onCancel }) => {
  return (
    <div className="h-full flex flex-col bg-gray-50/50">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
        <div>
          <h3 className="font-serif font-bold text-2xl text-gray-900 tracking-tight">Compare Items</h3>
          <p className="text-xs text-terracotta font-bold tracking-widest uppercase mt-1.5">Side-by-Side View</p>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
        <div className="flex gap-4 h-full overflow-x-auto snap-x snap-mandatory pb-4">
          {items.map((item, idx) => (
            <div
              key={item.id}
              className="min-w-[260px] w-full bg-white border border-gray-200 rounded-3xl p-5 flex flex-col shadow-sm hover:shadow-xl transition-all duration-500 animate-in zoom-in-95 snap-center transform hover:-translate-y-2"
              style={{ animationDelay: `${idx * 0.15}s` }}
            >
              <div className="h-40 bg-gray-100 rounded-2xl mb-5 overflow-hidden relative group shadow-inner">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                ) : <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>}
                {item.popular && (
                  <span className="absolute top-2 left-2 bg-gold/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">POPULAR</span>
                )}
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-bold text-gray-900 shadow-sm">
                  ₨{item.price}
                </div>
              </div>

              <h4 className="font-serif font-bold text-xl mb-2 leading-tight text-gray-900">{item.name}</h4>

              <div className="flex-grow space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-600 leading-relaxed border border-gray-100">
                  {item.description}
                </div>

                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-wider">Details</p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-[10px] font-bold border border-gray-200 capitalize">{item.category}</span>
                    {item.dietary?.length ? item.dietary.map(d => (
                      <span key={d} className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-bold border border-green-200">{d}</span>
                    )) : null}
                  </div>
                </div>
              </div>

              <button
                onClick={() => onAddToCart(item)}
                className="w-full bg-gray-900 hover:bg-black text-white py-3.5 rounded-xl font-bold text-sm mt-6 transition-all active:scale-95 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
              >
                Add to Order <Plus size={16} className="group-hover:rotate-90 transition-transform" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};